<template>
  <div>
    <h3>{{ companyPurpose !== 'rent' ? 'Product' : 'Unit' }} Categories</h3>

    <div v-if="isLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="custom-search d-flex justify-content-between">
        <b-form-group>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search by name"
            type="text"
            class="d-inline-block"
          />
        </b-form-group>
        <b-form-group v-if="canCreateProductCategories">
          <b-button
              variant="primary"
              class="mx-1 float-right"
              size="sm"
              @click="openUnitCategoryModal(null)"
          >
            <span> Add {{ companyPurpose !== 'rent' ? 'Product' : 'Unit' }} category</span>
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
          v-if="unitCategoriesTableView"
          :columns="displayColumns"
          :rows="filteredUnitCategories"
          :pagination-options="{
            enabled: true,
            perPage:pageLength
          }"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img v-if="props.row.picture !== null"
                :src="props.row.picture.includes('https') ? props.row.picture : '//' + props.row.picture"
                class="table-row-thumb"
                alt=""
            />
          </span>
          <span v-else-if="props.column.field === 'parent_category_id'">
            {{ props.row.parent_category_id ? unitCategories.find(c => c.id === props.row.parent_category_id)?.name : '' }}
          </span>
          <span v-else-if="props.column.field === 'action' && canChangeProductCategories" class="action-link-group unit-categories-action">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openUnitCategoryModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteUnitCategory(props.row)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <!-- pagination -->
        <template #pagination-bottom="props">
          <TablePagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value)=>props.pageChanged({currentPage:value})"
              @per-page-change="(n) => { pageLength = n; props.perPageChanged({ currentPerPage: n }) }"
            />
        </template>
      </vue-good-table>

      <b-card-group deck class="justify-content-left" v-if="!unitCategoriesTableView">
        <b-card
            v-for="unitCategory in unitCategories"
            :key="unitCategory.id"
            no-body
            :class="'unitCategory-block mb-2'"
            :img-src="unitCategory.picture !== null ? (unitCategory.picture.includes('https') ? unitCategory.picture : '//' + unitCategory.picture) : ''"
            img-alt="Image"
            img-top
        >
          <b-card-body>
            <b-card-title class="text-primary">{{ unitCategory.name }}</b-card-title>
            <b-card-text>{{ unitCategory.description }}</b-card-text>
          </b-card-body>

          <b-card-body class="d-flex flex-column-reverse">
            <b-button
                v-if="canChangeProductCategories"
                v-b-modal.edit-modal
                block
                variant="success"
                @click="openEditModal(unitCategory)"
            >
              <span class="text-light">Edit details</span>
            </b-button>
          </b-card-body>

        </b-card>
      </b-card-group>
    </template>
    <ManageUnitCategory
        :clickDate="clickDate"
        :unitCategory="unitCategory"
        :unitCategories="unitCategories"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import useVuelidate from '@vuelidate/core';
import { required, numeric, integer, minValue, minLength, email, requiredIf } from '@vuelidate/validators';
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardTitle,
  BCardText,
  BListGroup,
  BListGroupItem,
  BFormDatepicker,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
  BFormFile,
  BForm,
  BCardGroup,
  BSpinner,
  BFormTextarea, BFormCheckbox, BPagination, BImg,
  BFormSelect, BFormSelectOption,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import {$themeConfig} from "@themeConfig";
import ManageUnitCategory from "@/views/Components/ManageUnitCategory.vue";
import {backendErrorString} from "@/libs/utils";
import { canStaffCreate, canStaffChange } from '@/libs/staffPermissions'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'

export default {
  components: {
    TablePagination,
    ManageUnitCategory,
    DatatableCellValue,
    BImg,
    BPagination,
    BRow,
    BCol,
    BFormCheckbox,
    BCard,
    BCardBody,
    BCardTitle,
    BCardText,
    BListGroup,
    BListGroupItem,
    BFormDatepicker,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormFile,
    BForm,
    BCardGroup,
    BSpinner,
    BFormTextarea,
    vSelect,
    VueGoodTable,
    BFormSelect,
    BFormSelectOption,
    BDropdown,
    BDropdownItem,
  },
  data() {

    const { noUnitCategoryImage } = $themeConfig.app;

    return {
      unitCategory: null,
      clickDate: null,
      noUnitCategoryImage,
      unitCategoriesTableView: true,
      minDate: new Date(),
      isLoading: true,
      edited: {
        id: 0,
        name: null,
        description: null,
        picture: null,
        company_id: window.companyId > 0 ? window.companyId : null,
        parent_category_id: null,
        company: {},
      },
      temporaryPicture: null,
      isEditLoading: false,
      companyPurpose: window.companyPurpose,
      companyId: window.companyId,
      isTableLoading: true,
      searchTerm: '',
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          thClass: 'table-picture-col',
          tdClass: 'table-picture-col',
          sortable: false
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Description',
          field: 'description',
        },
        {
		  label: 'Parent Category',
		  field: 'parent_category_id',
		},
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center unit-categories-action-col',
          tdClass: 'text-center unit-categories-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
    }
  },
  computed: {
    ...mapGetters({
      unitCategories: 'unitCategories/unitCategories',
      companies: 'companies/companies',
      authUser: 'auth/user',
    }),
    canCreateProductCategories() {
      return canStaffCreate(this.authUser, 'product_categories')
    },
    canChangeProductCategories() {
      return canStaffChange(this.authUser, 'product_categories')
    },
    displayColumns() {
      if (this.canChangeProductCategories) {
        return this.columns
      }
      return this.columns.filter(column => column.field !== 'action')
    },
    filteredUnitCategories() {
      const keyword = String(this.searchTerm || '').trim().toLowerCase()
      if (!keyword) {
        return this.unitCategories
      }
      return (this.unitCategories || []).filter((category) => (
        String(category.name || '').toLowerCase().includes(keyword)
      ))
    },
  },
  async mounted() {

    await this.loadUnitCategories().catch(() => { toastFailure('Can not load unit categories') })
    this.isLoading = false
  },
  validations() {

    return {
      edited: {
        name: { required }
      }
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  methods: {
    ...mapActions({
      loadUnitCategories: 'unitCategories/loadUnitCategories',
      updateUnitCategory: 'unitCategories/updateUnitCategory',
      addUnitCategory: 'unitCategories/addUnitCategory',
      deleteUnitCategoryAction: 'unitCategories/deleteUnitCategory',
    }),
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      })
    },
    async deleteUnitCategory(unitCategory) {
      const label = this.companyPurpose !== 'rent' ? 'product category' : 'unit category'
      const message = `Are you sure you want to delete "${unitCategory.name}"? This cannot be undone.`
      if (!await this.confirmationMessage(message)) {
        return
      }

      this.deleteUnitCategoryAction(unitCategory.id)
        .then(() => {
          toastSuccess(`${label.charAt(0).toUpperCase() + label.slice(1)} has been deleted.`)
        })
        .catch((error) => {
          const data = error?.response?.data
          const apiMessage = data?.error?.[0] || data?.message
          toastFailure(apiMessage || `Failed to delete the ${label}.`)
        })
    },
    onContext(ctx) {
      this.formatted = ctx.selectedFormatted;
      this.selected = ctx.selectedYMD;
    },
    openUnitCategoryModal(row) {
      this.refreshUnitCategories().finally(() => {
        this.unitCategory = row;
        this.clickDate = new Date();
      });
    },
    openEditModal(unitCategory) {

      this.edited.id = unitCategory != null ? unitCategory.id : null
      this.edited.name = unitCategory != null ? unitCategory.name : null
      this.edited.description = unitCategory != null ? unitCategory.description : null
      this.edited.company_id = unitCategory != null ? unitCategory.company_id : null;
      this.edited.parent_category_id = unitCategory != null ? unitCategory.parent_category_id : null;
      this.edited.company = unitCategory != null ? this.companies.find(x => x.id === unitCategory.company_id) : (window.companyId > 0 ? window.companyId : null);
    },
    async editSubmit(event) {

      event.preventDefault();
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) {
        const errors = this.getValidationErrors();
        console.log('validation errors', errors); // You can handle the errors as you like (e.g., display them)
        return;
      } else {
        console.log('form ok', isFormCorrect); // You can handle the errors as you like (e.g., display them)
        return;
      }

      //await this.submitForm(this.edited.id > 0 ? 'edit' : 'add')
    },

    getValidationErrors() {

      const errors = [];
      // Access the validation states
      const fields = this.v$.edited;

      for (const field in fields) {
        if (fields[field].$error) {
          // Check if the field has an error and is not pending
          if (fields[field].$pending) continue; // Skip pending validations

          // Generate error messages based on the specific validation rules
          if (fields[field].required && !this.edited[field]) {
            errors.push(`${field} is required.`);
          }
          if (fields[field].minLength && this.edited[field]?.length < 3) {
            errors.push(`${field} must be at least 3 characters long.`);
          }
          // Add more validations and corresponding messages as needed
          // Example for numeric validation:
          if (fields[field].numeric && isNaN(this.edited[field])) {
            errors.push(`${field} must be a valid number.`);
          }
        }
      }
      return errors;
    },
    getTemporaryPicture() {
      return new Promise(resolve => {
        const reader = new FileReader()

        reader.addEventListener('load', () => {
          this.temporaryPicture = reader.result
          resolve()
        }, false)

        if (/\.(jpe?g|png|gif)$/i.test(this.edited.picture.name)) {
          reader.readAsDataURL(this.edited.picture)
        }
      })
    },
    refreshUnitCategories() {
      return this.loadUnitCategories().catch(() => {
        toastFailure('Can not load unit categories');
      });
    },
  },
}
</script>

<style scoped>
.unit-categories-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.unit-categories-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.unit-categories-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>